import { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { pl } from 'date-fns/locale';
import 'react-day-picker/style.css';

/**
 * Trigger-button + popover z DayPickerem. Bez natywnego <input type="date">
 * — klient publiczny nie może wpisać daty ręcznie (literówki w polskim
 * formacie DD.MM.YYYY vs amerykańskim MM/DD/YYYY są częstym źródłem błędów).
 *
 * Skalowanie kalendarza w `index.css` przez klasę `.rdp-large`
 * (responsive @media: większe komórki dla słabowidzących na lg/2xl).
 */

function toLocalISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map((n) => Number.parseInt(n, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function formatDateLong(isoDate: string): string {
  return parseLocalDate(isoDate).toLocaleDateString('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export type DatePickerPopoverProps = {
  /** ISO `YYYY-MM-DD` w lokalnej strefie czasu. */
  value: string;
  onChange: (iso: string) => void;
  /** Etykieta nad wybraną datą (klucz i18n już rozwiązany). */
  label: string;
  /** Min data włącznie (ISO `YYYY-MM-DD`). Default: dziś. */
  minDate?: string;
  /** Max data włącznie (ISO `YYYY-MM-DD`). Default: dziś + 60 dni (horyzont BE). */
  maxDate?: string;
};

/** Horyzont rezerwacji (CLAUDE.md sekcja 16 — default 60 dni). */
const MAX_BOOKING_HORIZON_DAYS = 60;

export function DatePickerPopover({ value, onChange, label, minDate, maxDate }: DatePickerPopoverProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(ev: PointerEvent) {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(ev.target as Node)) setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const today = parseLocalDate(toLocalISODate(new Date()));
  const minD = minDate ? parseLocalDate(minDate) : today;
  const maxD = (() => {
    if (maxDate) return parseLocalDate(maxDate);
    const m = new Date(today);
    m.setDate(m.getDate() + MAX_BOOKING_HORIZON_DAYS);
    return m;
  })();

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="group relative flex w-full items-center justify-between gap-4 rounded-lg bg-white p-6 text-left ring-1 ring-zinc-200 transition hover:bg-brand-50 hover:ring-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-zinc-900 dark:ring-zinc-700 dark:hover:bg-brand-900/30 dark:hover:ring-brand-400"
      >
        <div>
          <span className="block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            {label}
          </span>
          <span className="mt-1 block text-xl font-semibold text-zinc-900 first-letter:uppercase dark:text-zinc-100 lg:text-2xl">
            {formatDateLong(value)}
          </span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="h-7 w-7 shrink-0 text-zinc-400 transition group-hover:text-brand-500 dark:text-zinc-500 dark:group-hover:text-brand-400 lg:h-8 lg:w-8"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3.75 8.25h16.5M4.5 6h15a.75.75 0 0 1 .75.75v12a1.5 1.5 0 0 1-1.5 1.5h-13.5a1.5 1.5 0 0 1-1.5-1.5v-12A.75.75 0 0 1 4.5 6Z"
          />
        </svg>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={label}
          className="absolute left-1/2 z-30 mt-2 w-max -translate-x-1/2 rounded-xl bg-white p-3 shadow-xl ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700 lg:p-4"
        >
          <DayPicker
            className="rdp-large"
            mode="single"
            required
            selected={parseLocalDate(value)}
            defaultMonth={parseLocalDate(value)}
            onSelect={(d) => {
              if (d) onChange(toLocalISODate(d));
              setOpen(false);
            }}
            disabled={[{ before: minD }, { after: maxD }]}
            locale={pl}
            showOutsideDays
            weekStartsOn={1}
            fixedWeeks
          />
        </div>
      ) : null}
    </div>
  );
}
